import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesFeed, VoicesFeedPayload } from "../../../Models/voicesCampaigns";
import { useSaveVoicesFeed } from "../../../Query/useVoicesCampaigns";
import { ComoButton } from "../../Common/ComoButton";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";

interface Props {
  open: boolean;
  campaignId: number;
  feed: VoicesFeed | null;
  onClose: () => void;
}

const EMPTY_FEED: VoicesFeedPayload = { name: "", url: "", active: true };

const VoicesFeedDialog: React.FC<Props> = ({ open, campaignId, feed, onClose }) => {
  const { t } = useTranslation("voicesCampaigns");
  const saveFeed = useSaveVoicesFeed();
  const [form, setForm] = useState<VoicesFeedPayload>(EMPTY_FEED);

  useEffect(() => {
    if (!open) return;
    setForm(feed ? { name: feed.name, url: feed.url, active: feed.active } : EMPTY_FEED);
  }, [open, feed]);

  const handleSave = () => {
    saveFeed.mutate(
      { campaignId, feedId: feed ? feed.id : null, data: form },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>
        {feed ? t("editFeed") : t("newFeed")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <VoicesField label={t("feedName")}>
            <VoicesInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("feedUrl")} hint={t("feedUrlHint")}>
            <VoicesInput
              value={form.url}
              placeholder="https://www.example.com/rss"
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
          </VoicesField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <ComoButton
          onClick={handleSave}
          disabled={!form.name.trim() || !form.url.trim() || saveFeed.isPending}
        >
          {t("save")}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesFeedDialog;
